import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    InputAdornment,
    IconButton,
    Avatar,
    CircularProgress,
    useTheme,
    Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

// Custom Logo Component
const HydralyfeLogo: React.FC = () => {
    return (
        <Box
            sx={{
                width: 120,
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                position: 'relative',
            }}
        >
            <img 
                src="/images/hydra.png" 
                alt="Hydralyfe Logo" 
                style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                }} 
            />
        </Box>
    );
};

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const theme = useTheme();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            const response = await api.post('/api/login', { username, password });
            const { token, user } = response.data;
            login(token, user);
            navigate('/');
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.response?.data?.error || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: 'url(/images/bck2.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'relative',
                padding: 2,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark overlay for better readability
                    zIndex: 1
                }
            }}
        >
            <Container 
                maxWidth="xs" 
                sx={{ 
                    position: 'relative', 
                    zIndex: 2 // Place content above the overlay
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: 4
                    }}
                >
                    {/* Logo */}
                    <HydralyfeLogo />

                    {/* Title */}
                    <Typography 
                        component="h1" 
                        variant="h4" 
                        sx={{ 
                            fontWeight: 'bold',
                            color: 'white',
                            mb: 1,
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                        }}
                    >
                        Beauty Emergency Clinic Portal
                    </Typography>
                    
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            mb: 3, 
                            color: 'rgba(255,255,255,0.9)',
                            textAlign: 'center',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}
                    >
                        Sign in to your medical assistant dashboard
                    </Typography>
                </Box>

                <Paper
                    elevation={4}
                    sx={{
                        padding: 3,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)'
                    }}
                >
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                width: '100%', 
                                mb: 2,
                                borderRadius: 1
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="username"
                            label="Username"
                            name="username"
                            autoComplete="username"
                            autoFocus
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={isLoading}
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0,0,0,0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0,0,0,0.3)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.7)',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0,0,0,0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0,0,0,0.3)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ 
                                mt: 3, 
                                mb: 2,
                                py: 1.2,
                                backgroundColor: '#4169e1', // Royal blue to match Hydralyfe logo
                                color: 'white',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#3451b2',
                                },
                                position: 'relative',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                            }}
                            disabled={isLoading}
                            startIcon={<Lock />}
                        >
                            {isLoading ? (
                                <>
                                    <CircularProgress 
                                        size={24} 
                                        sx={{ 
                                            color: 'white',
                                            position: 'absolute',
                                            left: 20
                                        }} 
                                    />
                                    Signing in...
                                </>
                            ) : 'Sign in'}
                        </Button>
                    </Box>
                </Paper>

                <Divider sx={{ my: 4, opacity: 0.5, backgroundColor: 'rgba(255,255,255,0.3)' }} />

                {/* Footer */}
                <Typography 
                    variant="body2" 
                    align="center" 
                    sx={{ 
                        mt: 2, 
                        color: 'rgba(255,255,255,0.9)',
                        fontWeight: 500,
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                    }}
                >
                    Beauty Emergency Clinic Portal - Built by Drip Hydration
                </Typography>
                
                <Typography 
                    variant="caption" 
                    align="center" 
                    sx={{ 
                        mt: 1, 
                        display: 'block',
                        color: 'rgba(255,255,255,0.7)',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                    }}
                >
                    © {new Date().getFullYear()} Drip Hydration. All rights reserved.
                </Typography>
            </Container>
        </Box>
    );
};

export default LoginPage;
